import { ApolloClient, InMemoryCache } from '@apollo/client/core';
import { createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client';
import { from } from '@apollo/client';
import { typePolicies } from './cachePolicies';
import { auth } from '../../firebase';

const uri = process.env.REACT_APP_MEMBERSHIP_API_URL || 'http://localhost:4000/graphql';

const httpLink = createHttpLink({
    uri,
    // credentials: 'include', // uncomment if your API uses cookies
});

const authLink = setContext(async (_, { headers }) => {
    const user = auth.currentUser;
    let token = '';

    if (user) {
        try {
            token = await user.getIdToken();
        } catch (e) {
            console.error('Error getting ID token:', e);
        }
    }

    return {
        headers: {
            ...headers,
            ...(token && { authorization: `Bearer ${token}` }),
        },
    };
});

const errorLink = onError(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
        for (const gqlError of error.errors) {
            if (gqlError.message.includes('Store reset while query was in flight') ||
                gqlError.message.includes('not completed in link chain')) {
                if (process.env.NODE_ENV === 'development') {
                    console.debug('Suppressed expected Apollo GraphQL error during store reset:', {
                        message: gqlError.message,
                        path: gqlError.path,
                    });
                }
                return;
            }
        }
    } else {
        const status = (error as { statusCode?: number })?.statusCode;
        if (status === 401) {
            console.warn('Unauthorized (401). Consider prompting re-auth.');
        }

        const errorMessage = error.message || String(error);
        if (errorMessage.includes('Store reset while query was in flight') ||
            errorMessage.includes('not completed in link chain')) {
            if (process.env.NODE_ENV === 'development') {
                console.debug('Suppressed expected Apollo error during store reset:', {
                    message: errorMessage,
                    operation: operation?.operationName,
                });
            }
            return;
        }
    }
});

export const apolloClient = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
        typePolicies,
    }),
    defaultOptions: {
        // Use 'all' for queries to allow partial data (useful for resilience)
        query: { errorPolicy: 'all' },
        // Use 'none' for mutations - mutations should fail fast on errors
        // Individual mutations can override if needed, but default should be strict
        mutate: { errorPolicy: 'none' },
    },
    devtools: { enabled: process.env.NODE_ENV !== 'production' },
    assumeImmutableResults: true,
});